// ─── "From your coach" (Phase C / A1 + A4) ───
// Home card for a member with an active program assignment. `me.program` (from the
// org provider) is the summary; the full `my_program` response is cached in
// localStorage (PROGRAM_CACHE_KEY) and refreshed on every org refresh at most every
// 10 minutes, or straight away when the assignment changes / a `program` ping lands.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { V } from '../utils/theme';
import { LS } from '../utils/storage';
import { Icons } from './Icons';
import { Card, Btn, SuccessToastCtrl } from './ui';
import { today, fmtFull } from '../utils/helpers';
import { useOrg, orgApi, onOrgPing, personName } from '../utils/org';
import { useGymExercises, ExerciseDetailSheet, GymTag } from './GymExercises';
import { withGymExercises, gymEntriesToStore, isOrgExerciseId } from '../utils/orgExercises';
import { PROGRAM_CACHE_KEY, PROGRAM_REFRESH_MS, DAY_NAMES, scheduleFromProgram, nextTrainingDay, draftFromProgram, planView } from '../utils/program';

export function readProgramCache() {
  const c = LS.get(PROGRAM_CACHE_KEY);
  return c && typeof c === 'object' && c.assignment_id ? c : null;
}

// True when the member has a coach-assigned program and the `programs` flag is on.
export function useCoachPlan() {
  const { org, flags } = useOrg();
  return !!(org && org.program && org.program.assignment_id && flags.programs !== false);
}

export function useMyProgram() {
  const { org } = useOrg();
  const summary = org?.program || null; // { id, name, today_label, assignment_id, start_date, started }
  const assignmentId = summary?.assignment_id || null;
  const [cache, setCache] = useState(readProgramCache);
  const inflight = useRef(false);
  const load = useCallback(async (force) => {
    if (!assignmentId) { if (readProgramCache()) { LS.del(PROGRAM_CACHE_KEY); setCache(null); } return; }
    const cur = readProgramCache();
    // A record without `data` (markPrompted before any fetch, or a cleared one) is never fresh
    const fresh = cur && cur.data && cur.assignment_id === assignmentId && Date.now() - (cur.fetched_at || 0) < PROGRAM_REFRESH_MS;
    if ((fresh && !force) || inflight.current) return;
    inflight.current = true;
    try {
      const r = await orgApi('my_program');
      // A plan that has not started yet arrives as scheduled_assignment/scheduled_program with
      // assignment === null — still worth caching, the card shows it as upcoming.
      const id = r && ((r.assignment && r.assignment.id) || (r.scheduled_assignment && r.scheduled_assignment.id));
      if (!id) { LS.del(PROGRAM_CACHE_KEY); setCache(null); return; }
      const next = { assignment_id: id, fetched_at: Date.now(), data: r, prompted: cur?.prompted || null };
      LS.set(PROGRAM_CACHE_KEY, next); setCache(next);
    } catch { /* offline: keep the cache, the summary from `me` still renders */ }
    finally { inflight.current = false; }
  }, [assignmentId]);
  // `org` is a new object on every refreshOrg (realtime ping, foreground, join) — the
  // 10-minute window above keeps that from turning into a request per ping.
  useEffect(() => { load(false); }, [org, load]);
  useEffect(() => onOrgPing((p) => { if (p && p.kind === 'program') load(true); }), [load]);
  const markPrompted = useCallback(() => {
    const cur = readProgramCache() || { assignment_id: assignmentId, fetched_at: 0, data: null };
    const next = { ...cur, prompted: assignmentId };
    LS.set(PROGRAM_CACHE_KEY, next); setCache(next);
  }, [assignmentId]);
  const data = cache && cache.assignment_id === assignmentId ? cache.data : null;
  return { summary, data, prompted: cache?.prompted || null, markPrompted };
}

// The server's ISO start date ("2026-10-11") rendered for the member; never parsed for a date rule.
const startDateLabel = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? fmtFull(iso) : null);

export function CoachProgramCard({ s, d, todayW }) {
  const { org } = useOrg();
  const { summary, data, prompted, markPrompted } = useMyProgram();
  // The gym's own exercises (exerciseId 'org:<id>'): names, tips, instructions and demos
  const gymEx = useGymExercises();
  const [detail, setDetail] = useState(null);
  if (!summary) return null;
  // Read the theme inside render — applyBrand mutates V when a gym's colours land.
  const chip = { paddingTop: 3, paddingBottom: 3, paddingLeft: 8, paddingRight: 8, borderRadius: 6, background: V.inputBg, fontSize: 11, color: V.text2, border: `1px solid ${V.cardBorder}` };
  const program = data?.program || null;
  const dow = new Date().getDay();
  // The server decides which plan is running today; the member's local calendar only
  // picks the weekday label within that plan.
  const view = planView(data, { dow, fallbackLabel: summary.today_label,
    fallbackStarted: typeof summary.started === 'boolean' ? summary.started : null, fallbackStartsOn: summary.start_date || null });
  const upcoming = view.state === 'upcoming';
  const rest = view.state === 'rest';
  const label = view.label;
  const prescribed = view.exercises;
  const planName = view.name || summary.name;
  const startsOn = startDateLabel(view.startsOn);
  const coach = data?.coach || org?.coach || null;
  const coachName = coach ? personName(coach) : 'your coach';
  const askSchedule = view.canAdopt && prompted !== summary.assignment_id;
  const showStart = view.canStart && !todayW;

  const useSchedule = () => {
    if (!program) return; // a plan that has not started never becomes the member's schedule
    d({ type: 'SET_SCHEDULE', schedule: scheduleFromProgram(program.schedule) });
    d({type:'SET_ACTIVE_PROGRAM',activeProgram:null});
    LS.set('ft-active-coach-program', { id: 'coach:' + summary.assignment_id, name: summary.name, start: data?.assignment?.start_date || summary.start_date || today(), days: program.schedule, coach: true });
    markPrompted();
    SuccessToastCtrl.show(`${summary.name} applied to your schedule`);
  };
  const start = () => {
    // Gym exercises are logged like custom ones: keep a catalog copy in s.exercises first
    const stored = gymEntriesToStore(s.exercises, gymEx.list, prescribed);
    stored.forEach(ex => d({ type: 'ADD_EX', ex, replace: true }));
    const catalogue = withGymExercises(s.exercises, gymEx.list);
    for (const ex of stored) if (!catalogue.some(c => c.id === ex.id)) catalogue.push(ex);
    const draft = draftFromProgram(prescribed, { date: today(), assignment_id: summary.assignment_id, program_day: label, program_name: summary.name, exercises: catalogue });
    const missing = prescribed.length - draft.exercises.length;
    if (missing > 0) SuccessToastCtrl.show(`${missing} exercise${missing > 1 ? 's' : ''} not in your list — add ${missing > 1 ? 'them' : 'it'} by hand`);
    LS.set('ft-coach-draft', draft);
    d({ type: 'TAB', tab: 'log_workout' });
  };
  const next = rest ? nextTrainingDay(program ? program.schedule : null, dow) : null;

  // Card/Btn take no extra props — the test ids live on plain wrappers.
  return (
    <div data-testid="coach-program-card">
    <Card style={{ padding: 14, border: `1px solid ${V.accent}20`, background: `${V.accent}05` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: V.accent, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 2 }}>From your coach</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: V.text }} data-testid="coach-program-title">
            {upcoming ? `${planName} · ${startsOn ? `Starts ${startsOn}` : 'Not started yet'}` : `${planName} · Today: ${label}`}
          </div>
          <div style={{ fontSize: 11, color: V.text3, marginTop: 2 }}>
            {upcoming
              ? `Assigned by ${coachName} — no session from this plan until it starts`
              : rest
                ? (next ? `Rest day — next session: ${next.label} on ${DAY_NAMES[next.dow]}` : 'Rest day')
                : todayW ? 'Logged today ✓' : `${prescribed.length ? `${prescribed.length} exercise${prescribed.length === 1 ? '' : 's'} · ` : ''}Coach ${coachName}`}
          </div>
        </div>
        {showStart && (
          <Btn onClick={start} s={{ padding: '8px 18px', fontSize: 13, flexShrink: 0 }}>
            {Icons.dumbbell({ size: 13, color: V.bg })} Start
          </Btn>
        )}
      </div>
      {prescribed.length > 0 && !todayW && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 10 }}>
          {prescribed.map((ex, i) => {
            const id = ex.exerciseId;
            const g = isOrgExerciseId(id) ? gymEx.byId[id] : null;
            const name = (g && g.name) || ex.name || s.exercises.find(e => e.id === id)?.name || id;
            const text = `${name}${ex.sets ? ` ${ex.sets}×${ex.reps ?? ''}` : ''}`;
            const known = !!(g || s.exercises.some(e => e.id === id));
            if (!known) return <span key={i} style={chip}>{text}</span>;
            return (
              <button key={i} type="button" onClick={() => setDetail(id)} aria-label={`${text}: how to do it`} data-testid="coach-program-exercise"
                style={{ ...chip, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontFamily: V.font, WebkitTapHighlightColor: 'transparent' }}>
                {text}{g && <GymTag small />}
              </button>
            );
          })}
        </div>
      )}
      {askSchedule && (
        <div style={{ marginTop: 10, paddingTop: 8, paddingBottom: 8, paddingLeft: 10, paddingRight: 10, borderRadius: 8, background: V.inputBg, border: `1px solid ${V.cardBorder}` }} data-testid="coach-schedule-prompt">
          <div style={{ fontSize: 12, color: V.text, marginBottom: 6 }}>Your coach assigned «{summary.name}». Use its weekly schedule?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={useSchedule} s={{ minHeight: 34, padding: '6px 14px', fontSize: 12, flex: 1 }}>Yes</Btn>
            <Btn v="secondary" onClick={markPrompted} s={{ minHeight: 34, padding: '6px 14px', fontSize: 12, flex: 1 }}>No</Btn>
          </div>
        </div>
      )}
    </Card>
    {detail && <ExerciseDetailSheet id={detail} exercises={s.exercises} gym={gymEx.byId} onClose={() => setDetail(null)} onDemoError={gymEx.reload} />}
    </div>
  );
}
